import { FileJson, UploadCloud } from 'lucide-react';
import { useState } from 'react';
import { parseExplainJson } from '../lib/explain';
import type { ExplainImportResult } from '../types';
import { PlanTree } from './PlanTree';

const example = `[
  {
    "Plan": {
      "Node Type": "Limit",
      "Plan Rows": 20,
      "Total Cost": 16042.55,
      "Actual Rows": 20,
      "Actual Total Time": 84.21,
      "Plans": [
        {
          "Node Type": "Sort",
          "Plan Rows": 5400,
          "Total Cost": 16042.50,
          "Actual Rows": 20,
          "Actual Total Time": 84.19,
          "Plans": [
            {
              "Node Type": "Hash Join",
              "Hash Cond": "(o.customer_id = c.id)",
              "Plan Rows": 5400,
              "Total Cost": 15800.00,
              "Actual Rows": 5172,
              "Actual Total Time": 76.50,
              "Plans": [
                { "Node Type": "Seq Scan", "Relation Name": "orders", "Plan Rows": 500000, "Total Cost": 12000.00, "Actual Rows": 5100, "Actual Total Time": 64.00, "Filter": "status = 'completed'" },
                { "Node Type": "Hash", "Plan Rows": 100000, "Total Cost": 1800.00, "Plans": [{ "Node Type": "Seq Scan", "Relation Name": "customers", "Plan Rows": 100000, "Total Cost": 1800.00 }] }
              ]
            }
          ]
        }
      ]
    },
    "Planning Time": 1.02,
    "Execution Time": 84.89
  }
]`;

export function ExplainImporter() {
  const [text, setText] = useState(example);
  const [result, setResult] = useState<ExplainImportResult | null>(null);
  const [error, setError] = useState('');

  const parse = () => {
    try {
      setResult(parseExplainJson(text));
      setError('');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
      setResult(null);
    }
  };

  const loadFile = async (file?: File) => {
    if (!file) return;
    setText(await file.text());
  };

  return (
    <div className="import-grid">
      <section className="glass-card importer-input">
        <div className="section-title-row">
          <div>
            <span className="eyebrow">Evidence mode</span>
            <h2>Import a real execution plan</h2>
          </div>
          <FileJson size={26} />
        </div>
        <p>Paste PostgreSQL-compatible JSON generated by <code>EXPLAIN (ANALYZE, BUFFERS, FORMAT JSON)</code>. The file remains in your browser.</p>
        <textarea className="json-editor" value={text} onChange={(event) => setText(event.target.value)} spellCheck={false} />
        <div className="button-row">
          <button className="primary-button" onClick={parse}><FileJson size={18} /> Parse execution plan</button>
          <label className="secondary-button file-button"><UploadCloud size={18} /> Open JSON file<input type="file" accept="application/json,.json" onChange={(event) => loadFile(event.target.files?.[0])} /></label>
        </div>
        {error && <div className="error-box">{error}</div>}
      </section>

      <section className="glass-card imported-plan">
        {result ? (
          <>
            <div className="section-title-row">
              <div>
                <span className="eyebrow">{result.engine}</span>
                <h2>Observed plan tree</h2>
              </div>
              <div className="timing-pills">
                {result.planningTime !== undefined && <span>Planning {result.planningTime} ms</span>}
                {result.executionTime !== undefined && <span>Execution {result.executionTime} ms</span>}
              </div>
            </div>
            <div className="evidence-banner">This tree represents imported engine output. Unlike the estimator, it may include actual rows and timings.</div>
            <PlanTree node={result.root} />
            {result.notes.length > 0 && <ul className="notes-list">{result.notes.map((note) => <li key={note}>{note}</li>)}</ul>}
          </>
        ) : (
          <div className="empty-state"><FileJson size={44} /><h3>No plan imported yet</h3><p>Parse the example or upload your own EXPLAIN JSON.</p></div>
        )}
      </section>
    </div>
  );
}
